import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output , inject, signal } from '@angular/core';
import { ControlComponent, MapComponent as MapLibreMapComponent } from '@maplibre/ngx-maplibre-gl';
import { Map, MapStyleDataEvent } from 'maplibre-gl';
import { MapRouteDrawerService } from 'src/app/services/map/map-route-drawer.service';
import { MapRangeDrawerService } from 'src/app/services/map/map-range-drawer.service';
import { MapPaletteService } from 'src/app/services/map/map-palette.service';
import { MapService } from 'src/app/services/map/map.service';
import { NavigationMotionControllerService } from 'src/app/services/map/navigation-motion-controller.service';
import { CursorService } from 'src/app/services/map/cursor.service';
import { MapChargingStationDrawerService } from 'src/app/services/map/map-charging-station-drawer.service';
import { MapDrawerHelperService } from 'src/app/services/map/map-drawer-helper.service';
import { MapTurnDrawerService } from 'src/app/services/map/map-turn-drawer.service';
import { NavigationReplayService } from 'src/app/services/map/navigation-replay.service';
import { Route } from 'src/app/models/route';
import { ChargingStationDetails } from 'src/app/models/charging-station';
import { FollowModeState } from 'src/app/models/follow-mode-states';
import { LatLng } from 'src/app/models/latlng';
import { DisplayMode } from 'src/app/models/display-mode';
import { WindElement } from 'src/app/models/wind';
import { Waypoint } from 'src/app/models/range-params';
import { WindService } from 'src/app/services/map/wind.service';
import { WaypointService } from 'src/app/services/core/waypoint.service';
import { EvCalculationService } from 'src/app/services/core/ev-calculation.service';
import { SettingsService } from 'src/app/services/account/settings.service';
import { HttpService } from 'src/app/services/core/http.service';
import { MapPoiDrawerService } from 'src/app/services/map/map-poi-drawer.service';
import { MapReadyService } from 'src/app/services/map/map-ready.service';
import { NavigationService } from 'src/app/services/core/navigation.service';
import { LocationService } from 'src/app/services/platform/location.service';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { LanguageService } from 'src/app/services/account/language.service';
import { ActivatedRoute } from '@angular/router';
import { skip, Subscription } from 'rxjs';
import { Position } from 'src/app/models/position';
import { MapSettings } from 'src/app/models/map-settings';
import { MapSkin } from 'src/app/services/account/settings.service';
import { onMapSymbolScaleChange } from 'src/app/services/map/map-symbol-scale';
import { transformMapRequest } from 'src/app/services/map/map-request-transform';
import { FollowModeComponent } from './follow-mode/follow-mode.component';
import { StackedModalsService } from 'src/app/services/modal';
import { getMapMaxTileCacheSize, getMapPixelRatio } from 'src/app/services/platform/device-capability';
import { ConfirmationDialogComponent } from '../modals/confirmation-dialog/confirmation-dialog.component';

@Component({
  standalone: true,
  selector: 'app-map',
  imports: [
    MapLibreMapComponent, FollowModeComponent
  ],
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss']
})
export class MapComponent implements OnInit, OnDestroy {

  public mapInstance?: Map;
  @Output() mapLoaded = new EventEmitter<boolean>();
  @Output() mapClick = new EventEmitter<{ latitude: number, longitude: number }>();

  /**
   * Signal, not a plain field: the new style only reaches MapLibre through the
   * [mapStyle] binding, and this component sits inside an OnPush parent. Writing a
   * signal marks this view dirty on its own, so a skin change re-renders without
   * depending on something else refreshing the parent's subtree.
   */
  public readonly mapStyle = signal<string>('');
  public mapZoom: number = 14;
  public mapMinZoom: number = 3;
  public mapMaxZoom: number = 19;
  public mapCenter: [number, number] = [0, 0];
  public mapBearing: number = 0;
  public followModeState: FollowModeState = FollowModeState.TWO_D_FOLLOW;

  /**
   * Render budget. Both are clamped only on low-end hardware — see
   * device-capability.ts for why the drawing buffer is the allocation that
   * matters. NOTE: the map canvas deliberately does *not* set
   * `preserveDrawingBuffer`. It forces the browser to keep a second full-size
   * copy of the framebuffer and blocks the fast buffer-swap path, and nothing
   * reads the map canvas back (no toDataURL/toBlob/readPixels anywhere in the
   * app). Only re-enable it alongside code that actually captures the canvas.
   */
  public readonly mapPixelRatio = getMapPixelRatio();
  public readonly mapMaxTileCacheSize = getMapMaxTileCacheSize();

  /** Re-homes sprite and glyph requests where the environment proxies them; see map-request-transform.ts. */
  public readonly transformMapRequest = transformMapRequest;

  private followModeRange: boolean = true;
  private followModeMap: boolean = false;

  /** Navigation state */
  private navigationActive: boolean = false;
  private navigationRouteData: Route | null = null;
  private navigationSubscriptions: Subscription[] = [];
  private locationSubscription: Subscription | null = null;
  private skinSubscription: Subscription | null = null;
  private unitSubscription: Subscription | null = null;
  private languageSubscription: Subscription | null = null;
  private pointerScaleUnsubscribe: (() => void) | null = null;
  private lastRouteData: Route | null = null;
  private longPressCleanup: (() => void) | null = null;
  private onZoomEnd: (() => void) | null = null;
  private onWheel: ((event: unknown) => void) | null = null;
  private onPointerDown: ((event: PointerEvent) => void) | null = null;
  private onPointerUp: ((event: PointerEvent) => void) | null = null;

  /** Auto-restore of follow mode after panning during navigation */
  private readonly PANNED_REVERT_DELAY_MS = 5000;
  private readonly mapPaletteService = inject(MapPaletteService);
  private previousFollowModeState: FollowModeState = FollowModeState.TWO_D_FOLLOW;
  private pannedRevertTimer?: ReturnType<typeof setTimeout>;

  /** Handles for the post-paint charging-station init scheduled in onMapLoad(). */
  private chargingInitFrame?: number;
  private chargingInitTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private mapRouteDrawerService: MapRouteDrawerService,
    private mapRangeDrawerService: MapRangeDrawerService,
    private navigationMotionControllerService: NavigationMotionControllerService,
    private cursorService: CursorService,
    private mapService: MapService,
    private mapChargingStationDrawerService: MapChargingStationDrawerService,
    private mapDrawerHelperService: MapDrawerHelperService,
    private mapTurnDrawerService: MapTurnDrawerService,
    private navigationReplayService: NavigationReplayService,
    private navigationService: NavigationService,
    private locationService: LocationService,
    private utilsService: UtilsService,
    private languageService: LanguageService,
    private waypointService: WaypointService,
    private evCalculationService: EvCalculationService,
    private settingsService: SettingsService,
    private httpService: HttpService,
    private windService: WindService,
    private mapPoiDrawerService: MapPoiDrawerService,
    private mapReadyService: MapReadyService,
    private activatedRoute: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private stackedModalsService: StackedModalsService
  ) {
    const skin = this.settingsService.getMap().Skin;
    this.mapStyle.set(skin === MapSkin.Light ? 'assets/json/evlight.json' : 'assets/json/evdark.json?v=2');
  }

  ngOnInit(): void {
    this.mapCenter = [19.040236, 47.497913];

    // Detect demo/replay mode
    if (this.activatedRoute?.snapshot?.routeConfig?.path === 'demo') {
      this.navigationReplayService.replayMode = true;
    }

    // Subscribe to skin changes
    this.skinSubscription = this.settingsService.ObservableMap.pipe(skip(1)).subscribe((mapSettings: MapSettings | null) => {
      if (mapSettings) {
        const skin = mapSettings.Skin;
        const newMapStyle = skin === MapSkin.Light ? 'assets/json/evlight.json' : 'assets/json/evdark.json?v=2';
        // The POI toggle emits through the same subscription without changing
        // the style; only a real skin change reloads it and needs the re-init.
        const styleChanged = newMapStyle !== this.mapStyle();
        this.mapStyle.set(newMapStyle);

        if (this.mapInstance && !styleChanged) {
          this.mapService.applyPoiVisibility();
        } else if (this.mapInstance) {
          this.mapInstance.once('style.load', () => this.reinitMapLayersAfterStyleReload());
        }
      }
    });

    // Pointer-type changes (2-in-1 keyboard attach/detach, or the browser's
    // device-emulation toggle while debugging) resize the map symbols the way
    // CSS media queries already restyle the UI. See onMapSymbolScaleChange().
    this.pointerScaleUnsubscribe = onMapSymbolScaleChange(() => this.rebuildMapSymbolsForScale());

    // Re-render speed limits when unit settings change
    this.unitSubscription = this.settingsService.ObservableUnit.pipe(skip(1)).subscribe(() => {
      if (this.lastRouteData) {
        this.mapRouteDrawerService.redrawEVSpeedLimits(this.lastRouteData);
      }
    });

    // Re-render charge times on charger markers and the open charging-station popup when language changes
    this.languageSubscription = this.languageService.activeLanguage$.subscribe((loaded) => {
      if (loaded) {
        if (this.lastRouteData) {
          this.mapRouteDrawerService.redrawChargeTimes(this.lastRouteData);
        }
        this.mapChargingStationDrawerService.redrawOpenedChargingStationPopup();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.mapInstance) {
      if (this.onZoomEnd) {
        this.mapInstance.off('zoomend', this.onZoomEnd);
      }
      if (this.onWheel) {
        this.mapInstance.off('wheel', this.onWheel);
      }
      if (this.onPointerDown) {
        this.mapInstance.getCanvas().removeEventListener('pointerdown', this.onPointerDown);
      }
    }

    if (this.onPointerUp) {
      window.removeEventListener('pointerup', this.onPointerUp);
      this.onPointerUp = null;
    }
    this.clearPannedRevertTimer();

    // The map can be torn down before the deferred init in onMapLoad() fires.
    if (this.chargingInitFrame !== undefined) {
      cancelAnimationFrame(this.chargingInitFrame);
      this.chargingInitFrame = undefined;
    }
    if (this.chargingInitTimer !== undefined) {
      clearTimeout(this.chargingInitTimer);
      this.chargingInitTimer = undefined;
    }

    if (this.longPressCleanup) {
      this.longPressCleanup();
      this.longPressCleanup = null;
    }

    this.navigationMotionControllerService.destroyLocationSubscription();
    this.cursorService.reset();
    this.windService.clear();
    this.mapChargingStationDrawerService.destroy();
    this.exitNavigation();
    if (this.skinSubscription) {
      this.skinSubscription.unsubscribe();
    }
    if (this.unitSubscription) {
      this.unitSubscription.unsubscribe();
    }
    if (this.languageSubscription) {
      this.languageSubscription.unsubscribe();
    }
    if (this.pointerScaleUnsubscribe) {
      this.pointerScaleUnsubscribe();
      this.pointerScaleUnsubscribe = null;
    }

    if (this.mapInstance) {
      this.mapInstance.remove();
      this.mapInstance = undefined;
    }
  }

  /**
   * Rebuild the map layers after a full style reload (setStyle wipes every
   * image/source/layer). Shared by the skin switch and the pointer-type change,
   * since both need every cached symbol regenerated at the current settings.
   */
  private reinitMapLayersAfterStyleReload(): void {
    if (!this.mapInstance) return;

    this.mapService.applyPoiVisibility();

    // Re-initialize layers after style change
    this.mapChargingStationDrawerService.init(this.mapInstance);
    this.mapChargingStationDrawerService.setChargingStations();
    if (this.navigationActive) {
      // Navigation had hidden the off-route stations; re-hide them after the
      // full re-init above brought every station back.
      this.mapChargingStationDrawerService.hideNonRouteStations();
    }

    // setStyle() wiped the cursor image/source/layer; restore it at
    // its last known position with the current skin's icon
    const lastCursorPosition = this.cursorService.getLastCursorPosition();
    if (lastCursorPosition.longitude != null && lastCursorPosition.latitude != null) {
      const restoreCursor = () => this.cursorService.initCursorAt(
        lastCursorPosition.longitude!,
        lastCursorPosition.latitude!,
        this.mapInstance!
      );
      restoreCursor().catch(() => {
        // The style can still be settling right after style.load. A live
        // GPS/replay feed retries on its next position update, but a paused
        // replay never sends one — retry when the map goes idle instead.
        this.mapInstance?.once('idle', () => {
          restoreCursor().catch((error) => console.error('Failed to restore cursor after style change:', error));
        });
      });
    }

    // Redraw route if applicable
    if (this.navigationActive && this.navigationRouteData) {
      // The navigation route. lastRouteData below only holds the planning-time
      // route: stale after a replan, and never set at all when navigation is
      // entered directly (the /demo replay) — the road would stay missing,
      // since a paused replay produces no update that would redraw it.
      this.mapRouteDrawerService.drawRouteToMap(this.navigationRouteData);
      // The turn-arrow layers were wiped too. setRouteData resets the drawer's
      // dedupe id and startListening re-subscribes; ObservableNextTurn is a
      // BehaviorSubject, so the current turn's arrow redraws immediately even
      // while the replay is paused.
      this.mapTurnDrawerService.setRouteData(this.navigationRouteData);
      this.mapTurnDrawerService.startListening();
    } else if (this.waypointService.getSelectedDisplayMode() !== DisplayMode.RANGE) {
      const failedPlan = this.evCalculationService.getFailedPlan();
      if (failedPlan && failedPlan.failedPlan) {
        this.drawFailedRouteToMap(failedPlan.maxReachedWaypoint);
      } else if (this.lastRouteData) {
        this.drawRouteToMap(this.lastRouteData);
      }
    } else {
      // Range mode: the blob's layers were wiped with the style too.
      this.mapRangeDrawerService.redrawRange();
    }
  }

  /**
   * The pointer type flipped between desktop and touch, so the symbol scale
   * changed. Symbols are rasterized once and cached as GL textures keyed without
   * the scale, so the cleanest way to resize them all is a full style reload —
   * it wipes every cached image and reinitMapLayersAfterStyleReload() rebuilds
   * them at the new scale. Pointer flips are rare, so the brief reload is fine.
   */
  private rebuildMapSymbolsForScale(): void {
    if (!this.mapInstance) return;
    this.mapInstance.once('style.load', () => this.reinitMapLayersAfterStyleReload());
    this.mapInstance.setStyle(this.mapStyle(), { diff: false });
  }

  setFollowModeState(followModeState: FollowModeState) {
    if (followModeState === FollowModeState.PANNED && this.followModeState !== FollowModeState.PANNED) {
      this.previousFollowModeState = this.followModeState;
    }
    if (followModeState !== FollowModeState.PANNED) {
      this.clearPannedRevertTimer();
    }
    this.followModeState = followModeState;
    this.navigationMotionControllerService.setFollowModeState(followModeState);
    this.cdr.markForCheck();
  }

  /**
   * During navigation a pan only pauses following: once the map has been
   * untouched for PANNED_REVERT_DELAY_MS, return to the follow mode that was
   * active before the pan. Outside navigation, PANNED sticks until the user
   * re-centers manually.
   */
  private schedulePannedRevert(): void {
    this.clearPannedRevertTimer();
    if (!this.navigationActive || this.followModeState !== FollowModeState.PANNED) return;

    this.pannedRevertTimer = setTimeout(() => {
      this.pannedRevertTimer = undefined;
      if (this.navigationActive && this.followModeState === FollowModeState.PANNED) {
        this.setFollowModeState(this.previousFollowModeState);
      }
    }, this.PANNED_REVERT_DELAY_MS);
  }

  private clearPannedRevertTimer(): void {
    if (this.pannedRevertTimer !== undefined) {
      clearTimeout(this.pannedRevertTimer);
      this.pannedRevertTimer = undefined;
    }
  }

  /**
   * The first styledata event arrives in the same tick as the initial style.load — early enough
   * for the widget palette to tint the basemap before anything is painted (mapLoad is too late).
   */
  onStyleData(event: MapStyleDataEvent): void {
    this.mapPaletteService.hook(event.target);
  }

  onMapLoad(map: Map) {
    this.mapInstance = map;
    this.mapLoaded.emit(true);
    this.mapRouteDrawerService.setMapInstance(this.mapInstance!);
    this.mapRangeDrawerService.setMapInstance(this.mapInstance!);
    this.mapPoiDrawerService.setMapInstance(this.mapInstance!);
    this.mapService.setMapInstance(this.mapInstance!);
    this.mapDrawerHelperService.setMapInstance(this.mapInstance!);
    this.mapTurnDrawerService.setMapInstance(this.mapInstance!);

    this.mapService.applyPoiVisibility();

    this.onZoomEnd = () => {
      const zoom = this.mapInstance!.getZoom();
      this.navigationMotionControllerService.onUserZoomEnd(zoom);
      this.mapTurnDrawerService.onZoomChange();
    };
    this.mapInstance.on('zoomend', this.onZoomEnd);

    this.onPointerDown = () => {
      // Hold the revert timer while the user is touching the map; it restarts on release
      this.clearPannedRevertTimer();
      if (this.followModeState != FollowModeState.PANNED) {
        this.setFollowModeState(FollowModeState.PANNED);
      }
    };
    map.getCanvas().addEventListener('pointerdown', this.onPointerDown);

    // On window, not the canvas: a drag can end with the pointer released outside the map
    this.onPointerUp = () => {
      this.schedulePannedRevert();
    };
    window.addEventListener('pointerup', this.onPointerUp);

    this.onWheel = () => {
      if (this.followModeState != FollowModeState.PANNED) {
        this.setFollowModeState(FollowModeState.PANNED);
      }
      this.schedulePannedRevert();
    };
    map.on('wheel', this.onWheel);

    this.longPressCleanup = this.mapService.initLongPress((event) => {
      const selectedDisplayMode = this.waypointService.getSelectedDisplayMode();
      if (selectedDisplayMode === DisplayMode.RANGE || selectedDisplayMode === DisplayMode.ROUTE) {
        this.httpService.getReverseGeocode(event.latitude, event.longitude).subscribe({
          next: (resp: any) => {
            const addressString = this.mapService.parseAddressString(resp, event.latitude, event.longitude);
            const title = resp.street
              ? `${resp.street}${resp.housenumber ? ' ' + resp.housenumber : ''}`
              : addressString;
            this.mapPoiDrawerService.drawPoi(event.latitude, event.longitude, { address: addressString, title });
          },
          error: (err) => {
            this.stackedModalsService.openModalWindow(ConfirmationDialogComponent, {
              data: {
                title: this.languageService.languageJSON.Global_Error,
                message: this.languageService.languageJSON.Map_ReverseGeocodeError,
                buttons: [this.languageService.languageJSON.Global_Ok, null]
              }, autoFocus: false
            });

            console.error('Error fetching reverse geocode:', err);
          }
        });
      }
    });

    this.navigationMotionControllerService.initLocationSubscription();

    this.mapReadyService.setMapReady();

    // Deferred until after the first paint. Charging-station init rasterises the
    // station pin icons and starts the first tile fetch, and it is by far the
    // heaviest thing on this path — running it inline delayed the very frame
    // that tears down the loading screen. It is self-contained (it ends by
    // requesting its own bounding box, which draws the stations), and the
    // setChargingStations() / redrawCanvas() calls MainComponent makes on
    // map-ready both no-op safely until it has run.
    // rAF alone would still run before the paint it is queued against, hence the
    // nested timeout.
    this.chargingInitFrame = requestAnimationFrame(() => {
      this.chargingInitFrame = undefined;
      this.chargingInitTimer = setTimeout(() => {
        this.chargingInitTimer = undefined;
        if (this.mapInstance) {
          this.mapChargingStationDrawerService.init(this.mapInstance);
        }
      }, 0);
    });
  }

  public setChargingStations(): void {
    this.mapChargingStationDrawerService.setChargingStations();
  }

  public redrawChargingStationPopup(chargingStationPopup: ChargingStationDetails): void {
    this.mapChargingStationDrawerService.redrawChargingStationPopup(chargingStationPopup);
  }

  public drawRouteToMap(routeData: Route): void {
    this.lastRouteData = routeData;
    this.mapDrawerHelperService.removeRoutePlanningAnimation();
    this.mapRouteDrawerService.drawRouteToMap(routeData);
    this.mapChargingStationDrawerService.setLastRouteData(routeData);
  }

  public redrawCanvas(): void {
    this.mapChargingStationDrawerService.redrawCanvas();
  }

  public clearMapLayers(): void {
    this.mapDrawerHelperService.removeRoutePlanningAnimation();
    this.mapRouteDrawerService.clearRoute();
    this.mapRouteDrawerService.clearTrip();
    this.mapRangeDrawerService.clearRange();
    this.windService.clear();
  }

  public clearRange(): void {
    this.mapRangeDrawerService.clearRange();
    this.windService.clear();
  }

  public drawRoutePlanningAnimationToMap(waypoints: LatLng[]): void {
    this.mapRouteDrawerService.clearRoute();
    this.mapRangeDrawerService.clearRange();
    this.windService.clear();
    const skin = this.settingsService.getMap().Skin;
    this.mapDrawerHelperService.addRoutePlanningAnimation(waypoints, skin);
  }

  public drawFailedRouteToMap(maxReachedWaypoint: number): void {
    if (!this.mapInstance) return;

    this.mapDrawerHelperService.removeRoutePlanningAnimation();
    this.mapRouteDrawerService.clearRoute();
    this.mapRangeDrawerService.clearRange();

    const waypoints = this.waypointService.getWaypointsParams();
    if (waypoints.length < 2) return;

    const skin = this.settingsService.getMap().Skin;
    this.mapRouteDrawerService.drawFailedRoute(waypoints, skin, maxReachedWaypoint);
  }

  public initWind(windElements: WindElement[], animate = true): void {
    if (!this.mapInstance) return;
    this.windService.initWind(this.mapInstance, windElements, animate);
  }

  public positionMapOnSlidersPanelOpen(): void {
    if (!this.mapInstance) return;
    const moveValue = this.getMoveValue();
    this.mapInstance.panBy([0, moveValue], { duration: 500 });
  }

  public positionMapOnSlidersPanelClose(): void {
    if (!this.mapInstance) return;
    const moveValue = this.getMoveValue();
    this.mapInstance.panBy([0, -moveValue], { duration: 500 });
  }

  /**
   * Show the cursor at a point that is not a GPS fix. The motion controller
   * only places the cursor from position updates, so without a fix the range
   * would be drawn from the stored or default start with nothing marking that
   * origin. Moves the cursor if it already exists; the first real fix takes it
   * over from there.
   */
  public placeCursorAt(lng: number, lat: number): void {
    if (!this.mapInstance) return;

    if (this.cursorService.isCursorInitialized()) {
      this.cursorService.animateCursorTo(lng, lat, this.cursorService.getBearing());
      return;
    }

    const init = () => this.cursorService.initCursorAt(lng, lat, this.mapInstance!);
    init().catch(() => {
      // The style may still be loading right after the map came up; without a
      // GPS feed nothing else retries, so retry when the map goes idle.
      this.mapInstance?.once('idle', () => {
        init().catch((error) => console.error('Failed to place cursor:', error));
      });
    });
  }

  public setFollowModeRange(enabled: boolean): void {
    this.followModeRange = enabled;
  }

  public getFollowModeRange(): boolean {
    return this.followModeRange;
  }

  public onFollowModeClicked(): void {
    this.setFollowModeState(this.mapService.followModeStateChange(this.followModeState, this.mapInstance?.getPitch() ?? 0));
  }

  public userTriggeredMapFly(coords: Waypoint): void {
    if (!this.mapInstance) return;

    if (!this.followModeRange) {
      this.setFollowModeRange(true);
      return;
    }

    this.followModeMap = !this.followModeMap;

    if (this.followModeMap) {
      this.mapInstance.flyTo({
        center: [this.waypointService.getStartCoordsParams().lng, this.waypointService.getStartCoordsParams().lat],
        zoom: 16
      });
    } else {
      // Fit to range bounds if available, otherwise fly to coords
      this.mapInstance.flyTo({
        center: [coords.lng, coords.lat]
      });
    }
  }

  private getMoveValue(): number {
    let moveValue = 0;
    const portrait = window.innerHeight > window.innerWidth;

    if (portrait) {
      const bottomHeight = (window.innerHeight <= 550) ? 200 : 320;
      const topHeight = (window.innerWidth <= 380) ? 260 : 308;
      moveValue += bottomHeight;
      if (this.waypointService.getSelectedDisplayMode() === DisplayMode.ROUTE) {
        moveValue += topHeight;
      }
    } else {
      const bottomHeight = (window.innerHeight <= 550) ? 200 : 320;
      const topHeight = (window.innerWidth <= 620) ? 53 : 64;
      moveValue += bottomHeight;
      if (this.waypointService.getSelectedDisplayMode() === DisplayMode.ROUTE) {
        moveValue += topHeight;
      }
    }
    return moveValue / 2;
  }

  // ===== N A V I G A T I O N =====

  /**
   * Enter navigation mode: draw route with turn arrows, hide non-route elements.
   * Called by main component when route data arrives during navigation.
   */
  public setPlannedRoute(routeData: Route): void {
    const wasActive = this.navigationActive;
    this.navigationRouteData = routeData;
    this.navigationActive = true;

    // Entering navigation switches to 3D follow; replans keep the user's current mode
    if (!wasActive) {
      this.setFollowModeState(FollowModeState.THREE_D_FOLLOW);
    }

    // The planning pulse animation belongs to routing mode only; make sure it
    // is never left running once we are drawing the navigation route.
    this.mapDrawerHelperService.removeRoutePlanningAnimation();

    // Hide non-route charging stations and wind data
    this.windService.clear();
    this.mapChargingStationDrawerService.hideNonRouteStations();

    // Draw the navigation route
    this.mapRouteDrawerService.drawRouteToMap(routeData);

    // Set up turn arrow drawing
    this.mapTurnDrawerService.setRouteData(routeData);
    this.mapTurnDrawerService.startListening();

    // Initialize turn instructions
    routeData.Turns = this.mapService.initTurnIconAndInstruction(routeData.Turns);

    // Start navigation engine
    this.navigationService.StartNavigation(routeData);

    // Start GPS tracking (real or replay)
    this.startNavigationGps();

    // Subscribe to navigation events for replan/stop/exit
    this.subscribeNavigationEvents();
  }

  /** Re-center map on navigation cursor */
  public setToCenter(): void {
    this.navigationService.ObservableSetCenter.next(true);
    if (this.followModeState === FollowModeState.PANNED) {
      this.setFollowModeState(
        this.navigationActive ? this.previousFollowModeState : FollowModeState.TWO_D_FOLLOW
      );
    }
  }

  private startNavigationGps(): void {
    if (this.locationSubscription) {
      this.locationSubscription.unsubscribe();
    }

    if (this.navigationReplayService.replayMode) {
      this.navigationReplayService.startReplay(this.navigationRouteData!);
    }

    // In both replay and real mode, listen to location updates for navigation engine
    this.locationSubscription = this.locationService.ObservableLastPosition.subscribe((position: Position | null) => {
      if (position && this.navigationActive) {
        this.onNavigationGpsPosition(position);
      }
    });
  }

  private onNavigationGpsPosition(position: Position): void {
    if (!this.navigationRouteData) return;

    const closestPoint = this.navigationService.GetClosestPoint([position.Latitude, position.Longitude]);
    const snappedCoords = closestPoint[0];

    let bearing = position.Heading;
    if (closestPoint[1] !== -2) {
      bearing = this.navigationService.GetNavigationCursorAngle(closestPoint[1]);
    }

    let speed = 1;
    if (position.Speed != null) {
      speed = this.utilsService.mpsToKmph(position.Speed);
    }

    this.navigationService.RefreshTarget({
      Latitude: position.Latitude,
      Longitude: position.Longitude,
      Speed: speed,
      HorizontalAccuracy: 3,
      Course: bearing,
      FirstRoutePoint: false
    });
  }

  private subscribeNavigationEvents(): void {
    this.unsubscribeNavigationEvents();

    this.navigationSubscriptions.push(
      this.navigationService.ObservableChangeNavigation.subscribe((resp) => {
        if (!resp) return;
        if (resp === 'stop' || resp === 'arrive') {
          this.stopNavigationGps();
        } else if (resp === 'exit') {
          if (this.navigationReplayService.replayMode) {
            this.navigationReplayService.stop();
          }
        }
        // 'replan' is now handled by EvCalculationService.requestReplan,
        // called directly from NavigationService.Replan.
      })
    );

    this.navigationSubscriptions.push(
      this.navigationService.ObservableNextWaypointIdx.subscribe((resp) => {
        this.mapRouteDrawerService.setInactiveWaypoints(resp);
        if (resp != null) {
          this.waypointService.deleteWaypointWithoutReplan(1);
        }
      })
    );
  }

  private stopNavigationGps(): void {
    this.locationService.stopWatchPosition();
    if (this.locationSubscription) {
      this.locationSubscription.unsubscribe();
      this.locationSubscription = null;
    }
    this.locationService.setFakeGPSReplayMode(false);
  }

  /** Full exit from navigation mode: clean up everything */
  public exitNavigation(): void {
    const wasActive = this.navigationActive;
    this.navigationActive = false;
    this.navigationRouteData = null;
    this.clearPannedRevertTimer();
    if (wasActive) {
      this.setFollowModeState(FollowModeState.TWO_D_FOLLOW);
    }
    this.stopNavigationGps();
    this.navigationReplayService.stop();
    this.mapTurnDrawerService.clear();
    this.unsubscribeNavigationEvents();
    this.mapRouteDrawerService.setInactiveWaypoints(null);
    this.mapChargingStationDrawerService.showAllStations();
    // Display mode is owned by NavigationLifecycleHandler ('exit'/'arrive'
    // branches); setting it here would also fire on ngOnDestroy.
  }

  private unsubscribeNavigationEvents(): void {
    this.navigationSubscriptions.forEach(s => s.unsubscribe());
    this.navigationSubscriptions = [];
  }

}